import { Box, Button, Container, IconButton, List, ListItem, Paper, Stack, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import update from "immutability-helper";
import { getAllPages, updatePage } from "../../api/page";
import AdminLayout from "./AdminLayout";

interface NavItem {
    id: string;
    title: string;
    navOrder: number;
    navParent?: string;
}

interface DragItem {
    index: number;
    id: string;
    type: string;
}

interface SortableNavItemProps {
    item: NavItem;
    index: number;
    onUpdate: (id: string, navOrder: number) => void;
    moveItem: (dragIndex: number, hoverIndex: number) => void;
}

interface CollectedProps {
    handlerId: any;
}

const SortableNavItem = ({ item, index, onUpdate, moveItem }: SortableNavItemProps) => {
    const ref = useRef<HTMLLIElement>(null);

    const [{ handlerId }, drop] = useDrop<DragItem, void, CollectedProps>({
        accept: "NAV_ITEM",
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(dragItem: DragItem, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = dragItem.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveItem(dragIndex, hoverIndex);
            dragItem.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean }>({
        type: "NAV_ITEM",
        item: () => {
            return { id: item.id, index, type: "NAV_ITEM" };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0.4 : 1;
    drag(drop(ref));

    return (
        <ListItem
            ref={ref}
            style={{ opacity }}
            data-handler-id={handlerId}
            sx={{ bgcolor: "background.paper", mb: 1, borderRadius: 1, border: "1px solid #e0e0e0" }}
        >
            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                <IconButton size="small" sx={{ cursor: "grab" }}>
                    <DragIndicatorIcon />
                </IconButton>
                <Box flex={1}>
                    <Typography variant="body1">{item.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Order: {item.navOrder}
                    </Typography>
                </Box>
            </Stack>
        </ListItem>
    );
};

const NavbarEditor = () => {
    const [pages, setPages] = useState<BasicPageData[]>([]);
    const [navItems, setNavItems] = useState<NavItem[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadPages();
    }, []);

    const loadPages = () => {
        getAllPages()
            .then((pagesData) => {
                setPages(pagesData);
                const items = pagesData
                    .filter((p) => p.navOrder !== -1)
                    .map((p) => ({
                        id: p.id!,
                        title: p.title,
                        navOrder: p.navOrder,
                        navParent: p.navParent,
                    }))
                    .sort((a, b) => a.navOrder - b.navOrder);
                setNavItems(items);
            })
            .catch((err) => {
                console.error(err);
            });
    };

    const moveItem = useCallback((dragIndex: number, hoverIndex: number) => {
        setNavItems((prevItems) =>
            update(prevItems, {
                $splice: [
                    [dragIndex, 1],
                    [hoverIndex, 0, prevItems[dragIndex]],
                ],
            })
        );
    }, []);

    const updateNavOrder = (id: string, navOrder: number) => {
        setNavItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, navOrder } : item))
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Update all pages with new nav orders
            for (let i = 0; i < navItems.length; i++) {
                const item = navItems[i];
                const page = pages.find((p) => p.id === item.id);
                if (page) {
                    await updatePage(item.id, {
                        ...page,
                        navOrder: i,
                    });
                }
            }
            loadPages();
        } catch (error) {
            console.error("Failed to save navbar:", error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminLayout>
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Typography variant="h4" sx={{ mb: 3 }}>
                    Navbar Editor
                </Typography>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Drag and drop items to reorder them in the navbar. Set navOrder to -1 to hide a page from the navbar.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Pages with navOrder -1 will not appear in the navbar but can still be accessed directly via their URL.
                    </Typography>
                </Paper>

                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Navbar Items
                    </Typography>
                    <List>
                        {navItems.map((item, index) => (
                            <SortableNavItem
                                key={item.id}
                                item={item}
                                index={index}
                                onUpdate={updateNavOrder}
                                moveItem={moveItem}
                            />
                        ))}
                    </List>
                    {navItems.length === 0 && (
                        <Box sx={{ p: 4, textAlign: "center", bgcolor: "#f5f5f5", borderRadius: 1 }}>
                            <Typography variant="body1" color="text.secondary">
                                No pages in navbar. Edit pages and set navOrder to a value other than -1 to add them to the navbar.
                            </Typography>
                        </Box>
                    )}
                </Paper>

                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        size="large"
                    >
                        {saving ? "Saving..." : "Save Navbar Order"}
                    </Button>
                </Box>

                <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        All Pages
                    </Typography>
                    <List>
                        {pages.map((page) => (
                            <ListItem
                                key={page.id}
                                sx={{
                                    bgcolor: page.navOrder === -1 ? "#f5f5f5" : "background.paper",
                                    mb: 1,
                                    borderRadius: 1,
                                    border: "1px solid #e0e0e0",
                                }}
                            >
                                <Box flex={1}>
                                    <Typography variant="body1">{page.title}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Slug: {page.slug} | Nav Order: {page.navOrder} | Status: {page.live ? "Published" : "Draft"}
                                    </Typography>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Container>
        </AdminLayout>
    );
};

export default NavbarEditor;
